// apps/web/src/components/FeatureGate.tsx

import React from 'react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface FeatureGateProps {
  /**
   * Feature flag key to check
   */
  flag: string;

  /**
   * Content to render when feature is enabled
   */
  children: React.ReactNode;

  /**
   * Optional fallback content when feature is disabled
   * If not provided, nothing is rendered
   */
  fallback?: React.ReactNode;

  /**
   * Optional variant-specific rendering
   * Allows different content for different A/B test variants
   *
   * Example:
   * variants={{
   *   A: <OldBookingFlow />,
   *   B: <NewBookingFlow />,
   * }}
   */
  variants?: Record<string, React.ReactNode>;

  /**
   * Optional callback when feature is viewed
   * Useful for analytics tracking
   */
  onView?: (flagKey: string, isEnabled: boolean, variant?: string) => void;
}

/**
 * FeatureGate Component
 *
 * Conditionally renders content based on feature flag configuration.
 * Supports simple on/off flags and A/B testing with variants.
 *
 * Usage Examples:
 *
 * // Simple feature gate
 * <FeatureGate flag="dark-mode">
 *   <DarkModeToggle />
 * </FeatureGate>
 *
 * // Feature gate with fallback
 * <FeatureGate
 *   flag="new-search"
 *   fallback={<OldSearchBar />}
 * >
 *   <NewSearchBar />
 * </FeatureGate>
 *
 * // A/B testing with variants
 * <FeatureGate
 *   flag="booking-flow"
 *   variants={{
 *     A: <BookingFlowV1 />,
 *     B: <BookingFlowV2 />,
 *     control: <BookingFlowOriginal />,
 *   }}
 * />
 *
 * // With analytics tracking
 * <FeatureGate
 *   flag="featured-venues"
 *   onView={(flag, enabled, variant) => {
 *     trackEvent('feature_viewed', 'experiments', `${flag}:${variant}`);
 *   }}
 * >
 *   <FeaturedVenuesCarousel />
 * </FeatureGate>
 */
export function FeatureGate({
  flag,
  children,
  fallback = null,
  variants,
  onView,
}: FeatureGateProps) {
  const { isEnabled, variant } = useFeatureFlag(flag);

  // Track feature view (analytics)
  React.useEffect(() => {
    if (onView) {
      onView(flag, isEnabled, variant);
    }
  }, [flag, isEnabled, variant, onView]);

  // If variants are provided, use variant-based rendering
  if (variants) {
    const variantContent = variant ? variants[variant] : variants['control'];
    return <>{variantContent ?? fallback}</>;
  }

  // Simple on/off rendering
  if (isEnabled) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Higher-Order Component for feature gating
 *
 * Usage:
 * const FeatureGatedComponent = withFeatureGate(MyComponent, 'dark-mode');
 */
export function withFeatureGate<P extends object>(
  Component: React.ComponentType<P>,
  flagKey: string,
  fallback?: React.ComponentType<P>
): React.FC<P> {
  return function FeatureGatedComponent(props: P) {
    const { isEnabled } = useFeatureFlag(flagKey);

    if (isEnabled) {
      return <Component {...props} />;
    }

    if (fallback) {
      const FallbackComponent = fallback;
      return <FallbackComponent {...props} />;
    }

    return null;
  };
}
